import React, { useState, useEffect, useMemo } from "react";
import {
  MenuItem,
  FormControl,
  Select,
  Button,
  Box,
  Typography,
  Paper
} from "@material-ui/core";
import dayjs from "dayjs";
import { formatSeconds } from "@/helpers/utils";
import useStyles from "../../styles/deviceStyle";
import DeviceDetailSingleGraph from "./DeviceDetailSingleGraph";
import DeviceDetailConnectionGraph from "./DeviceDetailConnectionGraph";
import DeviceCpuTemperature from "./DeviceCpuTemperature";
import { useTranslation } from "../../langs/useTranslation";
import { Dialog, DialogTitle } from "@material-ui/core";
import {
  IconNoSignal,
  IconPoorSignal,
  IconFairSignal,
  IconGoodSignal,
  IconExcellentSignal,
  IconDisconnect,
  IconConnect,
  IconLan,
  IconCellular
} from "@/components/Icon";
import expandIcon from "../../assets/images/expand.svg";

import { getOperationApi } from "../../apis/deviceDetailAPI";
import SmallerLoadingIndicator from "../commons/SmallerLoadingIndicator";
import Tooltip from "@material-ui/core/Tooltip";
import GraphsDateTimePicker from "../GraphsDateTimePicker";
import { ArrowBack, ArrowForward } from "@material-ui/icons";

const DeviceDetailGraphs = props => {
  const { t } = useTranslation("device");
  const classes = useStyles();
  const { hardware, onlineStatus, deviceData, loading } = props;
  const [mins, setMins] = useState(1440);
  const [graphTitle, setGraphTitle] = useState("");
  const [type, setType] = useState("CPULoad");
  const [operationData, setOperationData] = useState({});
  const [operationLoading, setOperationLoading] = useState(true);
  const [twinsData, setTwinsData] = useState({});
  const [open, setOpen] = useState(false);
  const [dateInHour, setDateInHour] = useState(dayjs());

  const roundToTwo = num => {
    return +(Math.round(num + "e+2") + "e-2");
  };

  const expandHandler = e => {
    setGraphTitle(e);
    setType(e);
    setOpen(true);
    if (dateInHour.endOf("d").isBefore(dayjs().startOf("d"))) {
      setDateInHour(dayjs());
    }
  };

  const handlePrevHour = () => {
    setDateInHour(date => date.subtract(1, "h"));
  };

  const handleNextHour = () => {
    setDateInHour(date => date.add(1, "h"));
  };
  const prevHourBtnDisabled = useMemo(() =>
    !dateInHour.startOf('h').isAfter(dayjs().subtract(24, 'h').startOf("h")), [dateInHour])

  const nextHourBtnDisabled = useMemo(() =>
    !dateInHour.startOf('h').isBefore(dayjs().startOf("h")), [dateInHour])


  const switchGraphTitle = e => {
    switch (e) {
      case "connection":
        return t("deviceInformation.connection");
      case "CPULoad":
        return t("deviceInformation.CPULoading");
      case "CPUTemperature":
        return t("deviceInformation.CPUTemperature");
      case "RAMLoading":
        return t("deviceInformation.RAMLoading");
      case "storageUsage":
        return t("deviceInformation.storageUsage");
      case "network_wifi":
        return t({ defaultMessage: "Wi-Fi强度" });
      case "network_ethernet":
        return t({ defaultMessage: "有线网络" });
      case "network_cellular":
        return t({ defaultMessage: "移动网络" });
      default:
        break;
    }
  };

  const switchData = () => {
    switch (type) {
      case "connection":
        return (
          <DeviceDetailConnectionGraph
            onlineStatus={onlineStatus}
            mins={mins}
            dateInHour={dateInHour}
            graphType="connection"
          />
        );
      case "CPULoad":
        return (
          <DeviceDetailSingleGraph
            onlineStatus={onlineStatus}
            mins={mins}
            dateInHour={dateInHour}
            graphType="cpu_usage"
          />
        );
      case "CPUTemperature":
        return (
          <DeviceCpuTemperature
            onlineStatus={onlineStatus}
            hardware={hardware}
            mins={mins}
            dateInHour={dateInHour}
            graphType="cpu_temperature"
          />
        );
      case "RAMLoading":
        return (
          <DeviceDetailSingleGraph
            onlineStatus={onlineStatus}
            mins={mins}
            dateInHour={dateInHour}
            graphType="ram_usage"
          />
        );
      case "network_wifi":
      case "network_cellular":
      case "network_ethernet":
        return (
          <DeviceDetailSingleGraph
            onlineStatus={onlineStatus}
            mins={mins}
            dateInHour={dateInHour}
            graphType={type}
          />
        );
      case "storageUsage":
        return (
          <DeviceDetailSingleGraph
            onlineStatus={onlineStatus}
            mins={mins}
            dateInHour={dateInHour}
            graphType="storage_usage"
          />
        );

      default:
        break;
    }
  };

  const handleClose = () => {
    // setMins(30);
    setOpen(false);
  };

  const storagesUseValue = (drive = "") => {
    const availableCapacity =
      operationData?.storages?.[
      `${drive ? drive + "_" : drive}available_capacity`
      ];
    const totalCapacity =
      operationData?.storages?.[`${drive ? drive + "_" : drive}total_capacity`];
    if (
      availableCapacity &&
      totalCapacity &&
      availableCapacity.split(" ")[0] &&
      totalCapacity.split(" ")[0]
    ) {
      const usedCapacity =
        parseFloat(totalCapacity.split(" ")[0]) -
        parseFloat(availableCapacity.split(" ")[0]) *
        (availableCapacity.split(" ")[1] === "MB" ? 1 / 1024 : 1);
      return roundToTwo(usedCapacity);
    } else {
      return 0;
    }
  };

  const storageTotalValue = (drive = "") => {
    const totalCapacity =
      operationData?.storages?.[`${drive ? drive + "_" : drive}total_capacity`];
    if (totalCapacity && totalCapacity.split(" ")[0]) {
      return roundToTwo(parseFloat(totalCapacity.split(" ")[0]));
    } else {
      return 0;
    }
  };

  const loadingIndicator = operationLoading ? <SmallerLoadingIndicator /> : "-";

  const cpuTemperatureArea = () => {
    let num = 4;
    if (onlineStatus === "offline") {
      return (
        <>
          <div
            className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
          ></div>
          <div
            className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
          ></div>
          <div
            className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
          ></div>
          <div
            className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
          ></div>
        </>
      );
    } else {
      if (operationData?.cpuTemperature) {
        num = 4 - operationData?.cpuTemperature.length;
        return (
          <>
            {operationData?.cpuTemperature?.map((e, i) => (
              <div
                className={`${classes.cpuTemperatureOuter} ${e <= 50 ? classes.normalTemperature : classes.warnTemperature
                  }`}
                key={i}
              >
                <Typography
                  variant="h5"
                  className={`${e <= 50
                    ? classes.normalTemperature
                    : classes.warnTemperature
                    }`}
                >
                  {e}℃
                </Typography>
              </div>
            ))}
            {num >= 1 && (
              <div
                className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
              ></div>
            )}
            {num >= 2 && (
              <div
                className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
              ></div>
            )}
            {num >= 3 && (
              <div
                className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
              ></div>
            )}
            {num >= 4 && (
              <div
                className={`${classes.cpuTemperatureOuter} ${classes.noMoreCpu}`}
              ></div>
            )}
          </>
        );
      } else {
        return <div className="flex_center w-100">{loadingIndicator}</div>;
      }
    }
  };
  const storagesUsePercent = (drive = "") => {
    const availableCapacity =
      operationData?.storages?.[
      `${drive ? drive + "_" : drive}available_capacity`
      ];
    const totalCapacity =
      operationData?.storages?.[`${drive ? drive + "_" : drive}total_capacity`];
    if (
      availableCapacity &&
      totalCapacity &&
      availableCapacity.split(" ")[0] &&
      totalCapacity.split(" ")[0]
    ) {
      const usedCapacity =
        100 *
        ((parseInt(totalCapacity.split(" ")[0]) -
          parseInt(availableCapacity.split(" ")[0]) *
          (availableCapacity.split(" ")[1] === "MB" ? 1 / 1024 : 1)) /
          parseInt(totalCapacity.split(" ")[0]));
      return Math.round(usedCapacity);
    } else {
      return 0;
    }
  };

  const getOperation = async () => {
    try {
      setOperationLoading(true);
      let data = await getOperationApi(deviceData?.id);
      if (data && data.cpuLoading !== undefined) {
        setOperationData(data);
      }
    } finally {
      setOperationLoading(false);
    }
  };

  useEffect(() => {
    let intervalId;
    // 如果设备在线，一分钟更新一次
    if (!loading) {
      getOperation();
      if (onlineStatus !== "offline") {
        intervalId = setInterval(() => {
          getOperation();
        }, 60 * 1000);
      }
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [onlineStatus, loading]);

  return (
    <div className={classes.deviceDetailGraphs}>
      <div className={classes.operationArea}>
        <div className={classes.operationAreaFirstLine}>
          <Paper elevation={0} className={classes.operationSm}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" color="secondary">
                {t("deviceInformation.connection")}
              </Typography>
            </div>
            <Typography
              variant="h4"
              className={`${classes.onlineOfflineText} ${onlineStatus === "online" ? classes.online : classes.offline
                }`}
            >
              {onlineStatus ? (
                <div className={classes[onlineStatus]}>
                  {onlineStatus[0].toUpperCase() + onlineStatus.slice(1)}
                </div>
              ) : (
                <SmallerLoadingIndicator />
              )}
            </Typography>
            <div
              className={classes.expandIcon}
              onClick={() => expandHandler("connection")}
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
          <Paper elevation={0} className={classes.operationSm}>
            <Typography variant="body1" color="secondary">
              {t("deviceInformation.CPULoading")}
            </Typography>
            {onlineStatus === "offline" ? (
              <Typography
                variant="h4"
                className={`${classes.warnOrNotText} ${classes.notWarn}`}
              >
                -
              </Typography>
            ) : (
              <Typography
                variant="h4"
                className={`${classes.warnOrNotText} ${operationData?.cpuLoading <= 50
                  ? classes.notWarn
                  : classes.warn
                  }`}
              >
                {operationData?.cpuLoading || operationData?.cpuLoading === 0
                  ? `${operationData?.cpuLoading}%`
                  : loadingIndicator}
              </Typography>
            )}
            <div
              className={classes.expandIcon}
              onClick={() => expandHandler("CPULoad")}
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
          <Paper elevation={0} className={classes.operationSm}>
            <Typography variant="body1" color="secondary">
              {t("deviceInformation.RAMLoading")}
            </Typography>
            {onlineStatus === "offline" ? (
              <Typography
                variant="h4"
                className={`${classes.warnOrNotText} ${classes.notWarn}`}
              >
                -
              </Typography>
            ) : (
              <Typography
                variant="h4"
                className={`${classes.warnOrNotText} ${operationData?.ramLoading <= 50
                  ? classes.notWarn
                  : classes.warn
                  }`}
              >
                {operationData?.ramLoading || operationData?.ramLoading === 0
                  ? `${operationData?.ramLoading}%`
                  : loadingIndicator}
              </Typography>
            )}
            <div
              className={classes.expandIcon}
              onClick={() => expandHandler("RAMLoading")}
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
          <Paper elevation={0} className={classes.operationSm}>
            <Typography variant="body1" color="secondary">
              {(operationData?.network?.type === "network_wifi" ||
                !operationData?.network) &&
                t({ defaultMessage: "Wi-Fi强度" })}
              {operationData?.network?.type === "network_cellular" &&
                t({ defaultMessage: "移动网络" })}
              {operationData?.network?.type === "network_ethernet" &&
                t({ defaultMessage: "有线网络" })}
            </Typography>
            {onlineStatus === "offline" ? (
              <Typography
                variant="h4"
                className={`${classes.warnOrNotText} ${classes.notWarn}`}
              >
                -
              </Typography>
            ) : operationData?.network !== undefined ? (
              <>
                <div>
                  {(function () {
                    let Icon = IconNoSignal;
                    const networkType = operationData?.network.type;
                    if (networkType === "network_ethernet") {
                      Icon = IconLan;
                    } else if (networkType === "network_cellular") {
                      Icon = IconCellular;
                    } else {
                      const wifiSignal = operationData?.network.value;
                      if (wifiSignal > -60) Icon = IconExcellentSignal;
                      if (wifiSignal <= -60) Icon = IconGoodSignal;
                      if (wifiSignal < -70) Icon = IconFairSignal;
                      if (wifiSignal < -80) Icon = IconPoorSignal;
                      if (wifiSignal < -90) Icon = IconNoSignal;
                    }
                    return <Icon size={30} />;
                  })()}
                </div>
                {operationData?.network !== undefined && (
                  <Typography
                    color="secondary"
                    style={{
                      width: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center"
                    }}
                  >
                    {operationData.network?.name}
                  </Typography>
                )}
              </>
            ) : (
              loadingIndicator
            )}
            <div
              className={classes.expandIcon}
              onClick={() =>
                expandHandler(operationData?.network?.type || "network_wifi")
              }
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
          <Paper
            elevation={0}
            className={classes.operationSm}
            style={{ position: "relative" }}
          >
            <Typography variant="body1" color="secondary">
              {t({ defaultMessage: "运作时长" })}
            </Typography>
            {operationData?.runtimeDuration !== undefined ? (
              <>
                <Tooltip
                  title={t(
                    { defaultMessage: "最后更新时间：{lastUpdate}" },
                    {
                      lastUpdate:
                        dayjs(operationData?.runtimeDuration?.update_at).format(
                          "YYYY/MM/DD HH:mm:ss"
                        ) || "-"
                    }
                  )}
                >
                  <Typography
                    variant="h4"
                    className={`${classes.warnOrNotText} ${classes.notWarn}`}
                  >
                    {formatSeconds(
                      onlineStatus === "offline"
                        ? 0
                        : operationData?.runtimeDuration?.runtime_total || 0
                    )}
                  </Typography>
                </Tooltip>
                <Typography
                  color="secondary"
                  className={`${classes.warnOrNotText} ${classes.notWarn}`}
                >
                  Day: Hour: Minute
                </Typography>
              </>
            ) : (
              loadingIndicator
            )}
            <div style={{ height: 24 }} />
          </Paper>
        </div>
        <div className={classes.operationAreaSecondLine}>
          <Paper elevation={0} className={classes.operationLg}>
            <Typography variant="body1" color="secondary">
              {t("deviceInformation.CPUTemperature")}
            </Typography>
            <div className={classes.cpuTemperatureArea}>
              {cpuTemperatureArea()}
            </div>
            <div
              className={classes.expandIcon}
              onClick={() => expandHandler("CPUTemperature")}
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
          <Paper elevation={0} className={classes.operationLg}>
            <Typography variant="body1" color="secondary">
              {t("deviceInformation.hardwareStorageTitle")}
            </Typography>
            {(operationData?.storages?.available_capacity ||
              operationData?.storages?.available_capacity === 0) &&
              (operationData?.storages?.total_capacity ||
                operationData?.storages?.total_capacity === 0) ? (
              <div className={classes.driveContainer}>
                <Tooltip title={`${storagesUsePercent()}%`} placement="bottom">
                  <div className={classes.driveWrapper}>
                    <Typography
                      className={classes.driveTitle}
                      variant="h5"
                      style={{ marginRight: 16 }}
                    >
                      {t({ defaultMessage: "整体" })}
                    </Typography>
                    <div>
                      <div style={{ display: "flex" }}>
                        <Typography
                          variant="h4"
                          className={`${classes.warnOrNotText} ${storagesUsePercent() > 50
                            ? classes.warn
                            : classes.notWarn
                            }`}
                        >
                          {storagesUseValue()
                            ? storagesUseValue() + " GB"
                            : "- GB"}
                        </Typography>
                        <Typography
                          variant="h4"
                          style={{ margin: "0 5px" }}
                          className={`${classes.warnOrNotText} ${classes.notWarn}`}
                        >
                          /
                        </Typography>
                        <Typography
                          variant="h4"
                          className={`${classes.warnOrNotText} ${classes.notWarn}`}
                        >
                          {operationData?.storages?.total_capacity || "- GB"}
                        </Typography>
                      </div>
                      <div className={classes.storageLine}>
                        <div
                          style={{
                            width: `${storagesUsePercent()}%`,
                            background:
                              storagesUsePercent() > 50 ? "#f98746" : "#4BC0C0"
                          }}
                          className={classes.storageLineColor}
                        ></div>
                      </div>
                    </div>
                  </div>
                </Tooltip>
                {deviceData?.os_type === "windows" && (
                  <Tooltip
                    title={`${storagesUsePercent("c")}%`}
                    placement="bottom"
                  >
                    <div className={classes.driveWrapper}>
                      <Typography
                        className={classes.driveTitle}
                        variant="h5"
                        style={{ marginRight: 16 }}
                      >
                        {t({ defaultMessage: "C槽" })}
                      </Typography>

                      <div>
                        <div style={{ display: "flex" }}>
                          <Typography
                            variant="h4"
                            className={`${classes.warnOrNotText} ${storagesUsePercent("c") > 50
                              ? classes.warn
                              : classes.notWarn
                              }`}
                          >
                            {storagesUseValue("c")
                              ? storagesUseValue("c") + " GB"
                              : "- GB"}
                          </Typography>
                          <Typography
                            variant="h4"
                            style={{ margin: "0 5px" }}
                            className={`${classes.warnOrNotText} ${classes.notWarn}`}
                          >
                            /
                          </Typography>
                          <Typography
                            variant="h4"
                            className={`${classes.warnOrNotText} ${classes.notWarn}`}
                          >
                            {storageTotalValue("c")
                              ? storageTotalValue("c") + " GB"
                              : "- GB"}
                          </Typography>
                        </div>
                        <div className={classes.storageLine}>
                          <div
                            style={{
                              width: `${storagesUsePercent("c")}%`,
                              background:
                                storagesUsePercent("c") > 50
                                  ? "#f98746"
                                  : "#4BC0C0"
                            }}
                            className={classes.storageLineColor}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (
              loadingIndicator
            )}
            <div
              className={classes.expandIcon}
              onClick={() => expandHandler("storageUsage")}
            >
              <img src={expandIcon} alt="expand_more" />
            </div>
          </Paper>
        </div>
      </div>
      <Dialog
        maxWidth={"xl"}
        onClose={handleClose}
        open={open}
        className={`${classes.modal}`}
      >
        <DialogTitle align="center">
          {t("deviceInformation.systemTitle")}
        </DialogTitle>
        <div className={classes.graphTitleWithSelect}>
          <Typography
            variant="h6"
            className={classes.deviceDetail_SectionTitle}
          >
            {switchGraphTitle(graphTitle)}
          </Typography>
          <div className="flex_center">
            {/* <Typography variant="body2">
              {t("deviceDetail.inThePast")}
            </Typography>
            <FormControl className={classes.getTimeSelect}>
              <Select
                variant="outlined"
                value={mins}
                onChange={event => setMins(event.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value={30}>0.5</MenuItem>
                <MenuItem value={60}>1</MenuItem>
                <MenuItem value={360}>6</MenuItem>
                <MenuItem value={720}>12</MenuItem>
                <MenuItem value={1440}>24</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2">{t("deviceDetail.hours")}</Typography> */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBack />}
              disabled={prevHourBtnDisabled}
              onClick={handlePrevHour}
            >
              {t({ defaultMessage: "上一小时" })}
            </Button>
            <Button
              style={{ marginLeft: 16, marginRight: 16 }}
              size="small"
              variant="outlined"
              startIcon={<ArrowForward />}
              disabled={nextHourBtnDisabled}
              onClick={handleNextHour}
            >
              {t({ defaultMessage: "下一小时" })}
            </Button>
            <GraphsDateTimePicker value={dateInHour} onChange={setDateInHour} />
          </div>
        </div>
        {switchData()}
      </Dialog>
    </div>
  );
};

export default React.memo(DeviceDetailGraphs);
